import { api } from '../../lib/api';
import type { TrackSummary } from '../tracks/TrackList';

/**
 * The one line under a card saying why it is on the page.
 *
 * The tone names are the design's, not the data's: `fresh` is green, `follows`
 * is indigo, `rising` is red. The server picks which of the three a track earns,
 * so the rule lives in one place rather than being re-derived by every template
 * that wants to show a tag.
 */
export interface TrackTag {
  label: string;
  tone: 'fresh' | 'follows' | 'rising';
}

export interface DashboardTrack extends TrackSummary {
  tag: TrackTag | null;
  /**
   * Somebody chose this rather than the page finding it.
   *
   * Only ever set on the hero. It changes what the card says about itself -
   * "dropped four days ago" is true of a track the query found by looking at
   * the last month and nonsense over one picked out of the back catalogue.
   */
  pinned?: boolean;
  owner: {
    id: number;
    username: string;
    display_name: string;
    verified?: boolean;
    created_at: string | null;
    avatar: string | null;
    /*
     * Null means the query behind this row did not join `profiles`, not that
     * the artist has no followers. The follow feed is one such query, which is
     * why the hero checks for null before rendering the count at all.
     */
    follower_count: number | null;
  };
}

export interface UndiscoveredArtist {
  id: number;
  username: string;
  display_name: string;
  verified?: boolean;
  avatar: string | null;
  follower_count: number;
  track_count: number;
  first_track_at: string | null;
  /** Why they are worth a listen, or null when there is nothing true to say. */
  signal: string | null;
}

export interface Reach {
  tracks: number;
  plays: number;
  unique_listeners: number;
  plays_7d: number;
  listeners_7d: number;
  followers: number;
  saves: number;
}

export interface Dashboard {
  featured: DashboardTrack | null;
  undiscovered: UndiscoveredArtist[];
  /*
   * Both optional, and absent rather than empty when there is nothing to say.
   *
   * A new account has saved nothing and released nothing, and rendering "Your
   * reach: 0" at somebody on their first visit reads as a scoreboard telling
   * them they are losing. The page checks for the key and leaves the whole
   * section out.
   */
  recommended?: {
    seed: {
      id: number;
      title: string;
      slug: string;
      owner_username: string;
      owner_display_name: string; verified?: boolean;
      genre: string | null;
    };
    basis: 'genre' | 'popular';
    items: DashboardTrack[];
  };
  reach?: Reach;
}

export type DashboardTab = 'all' | 'fresh' | 'following';

export interface DashboardTracks {
  tab: DashboardTab;
  /** Only on the `following` tab; null elsewhere. */
  following_count: number | null;
  items: DashboardTrack[];
}

export const dashboard = {
  summary: () => api.get<Dashboard>('/dashboard'),
  tracks: (tab: DashboardTab) => api.get<DashboardTracks>(`/dashboard/tracks?tab=${tab}`),
};

/** `4:07`, or an em dash while the duration is still unknown. */
export function duration(ms: number | null): string {
  if (ms === null) return '—';

  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * "40 minutes ago", for the hero's dropped-at line.
 *
 * Coarse on purpose. The claim the hero is making is "this is new", and
 * "37 minutes ago" is a precision nobody asked for that also makes the page
 * look stale the moment it is left open.
 */
export function since(iso: string): string {
  const ms = Date.now() - new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z')).getTime();
  const minutes = Math.round(ms / 60000);

  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);

  if (hours < 24) return hours === 1 ? 'an hour ago' : `${hours} hours ago`;

  const days = Math.round(hours / 24);

  if (days < 7) return days === 1 ? 'yesterday' : `${days} days ago`;

  const weeks = Math.round(days / 7);

  return weeks === 1 ? 'last week' : `${weeks} weeks ago`;
}
